import { EndpointBuilder } from '@reduxjs/toolkit/query';
import {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/query';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { CACHING_TIME } from '@/store/utils';
import {
  DeviceDetailResponse,
  DownloadCSVResponse,
  MeterStatisticsResponse,
  PingTestStatusResponse,
  UploadFactoryFileResponse
} from '../../types';
import {
  PingTestPayload,
  UpdateDevicePayload,
  UploadFactoryFilePayload
} from '../../types/records/device-information';
import { DownloadCSVRecord } from '../../types/records/download-csv';

export const deviceInfoEndpoints = (
  builder: EndpointBuilder<
    BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError,
      NonNullable<unknown>,
      FetchBaseQueryMeta
    >,
    string,
    'hesApi'
  >
) => ({
  getDeviceInfo: builder.query<DeviceDetailResponse, { searchQuery: string }>({
    query: ({ searchQuery }) => ({
      url: `device-management/device-info${searchQuery}`,
      method: 'GET'
    }),
    providesTags: ['DeviceInfo'],
    keepUnusedDataFor: CACHING_TIME
  }),
  pingTest: builder.mutation<
    DownloadCSVRecord | PingTestStatusResponse,
    PingTestPayload
  >({
    query: (body) => ({
      url: `device-management/ping-registered-device`,
      method: 'POST',
      body,
      responseHandler: async (response) => {
        const contentType = response.headers.get('content-type');

        // If the content type is JSON, parse the response as JSON
        if (contentType?.includes('application/json')) {
          return await response.json(); // Parse and return JSON
        }

        // Otherwise, treat it as a Blob (for files like CSV)
        return await response.blob(); // Handle as Blob (binary data)
      }
    }),
    transformResponse: (
      response: Blob | DownloadCSVResponse | PingTestStatusResponse,
      meta,
      arg
    ): DownloadCSVRecord | PingTestStatusResponse => {
      if (arg.isBulkPingTest) {
        const contentType = meta?.response?.headers?.get('content-type');
        console.log(response);
        console.log(contentType);

        // Check if the content type is CSV or text, indicating a file download
        if (
          contentType?.includes('text/csv') ||
          contentType?.includes('application/octet-stream')
        ) {
          // If the response is a Blob (CSV file), return it as a csvBlob
          return { csvBlob: response as Blob };
        }

        // If it's a JSON response, handle it as DownloadCSVResponse
        if ('data' in response) {
          const downloadResponse = response as DownloadCSVResponse;
          const url = downloadResponse?.data?.records[0]?.url;
          return { url };
        }

        // If neither case matches, throw an error or handle accordingly
        throw new Error('Unexpected response format');
      }
      return response as PingTestStatusResponse;
    }
  }),

  updateDeviceInfo: builder.mutation<void, UpdateDevicePayload>({
    query: (data) => ({
      url: `device-management/update-device`,
      method: 'PUT',
      body: data
    }),
    invalidatesTags: ['DeviceInfo']
  }),
  uploadFactoryFile: builder.mutation<
    UploadFactoryFileResponse,
    UploadFactoryFilePayload
  >({
    queryFn: async ({ formData, onProgress, signal }) => {
      const baseUrl = `${import.meta.env.VITE_HES_BASE_URL}/${
        import.meta.env.VITE_HES_API_VERSION
      }/device-management/upload-factory-file`;

      return new Promise((resolve, reject) => {
        // Initialize XMLHttpRequest
        const xhr = new XMLHttpRequest();

        // Open the request
        xhr.open('POST', baseUrl);

        // Set Authorization header if token exists
        const token = localStorage.getItem('hes_token');
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        // Monitor the upload progress
        xhr.upload.onprogress = function (e: ProgressEvent<EventTarget>) {
          const percentComplete = Math.ceil((e.loaded / e.total) * 100);
          onProgress(percentComplete); // Update progress
        };

        // Handle successful response
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ data: JSON.parse(xhr.responseText) });
          } else {
            reject(new Error(xhr.responseText || 'Unknown upload error'));
          }
        };

        // Handle errors during the request
        xhr.onerror = () => {
          reject(new Error(xhr.responseText || 'Unknown upload error'));
        };

        // Listen for request cancellation via AbortSignal
        signal.addEventListener('abort', () => {
          xhr.abort(); // Abort the XMLHttpRequest
          reject({ error: { status: xhr.status, data: 'Upload aborted!' } });
        });

        // Send the formData to the server
        xhr.send(formData);
      });
    }
  }),
  getMeterStatistics: builder.query<
    MeterStatisticsResponse,
    { searchQuery: string }
  >({
    query: ({ searchQuery }) => ({
      url: `first-data-point/info${searchQuery}`,
      method: 'GET'
    })
  })
});
